import React from 'react';
import { TCAction } from 'src/api/generated';
import { Text } from 'src/modules/shared/components/atoms/text';
import LabelTextArea from 'src/modules/shared/components/atoms/inputs/label-text-area';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import { WithTooltip } from 'src/modules/automations/components/atoms/help-tooltip/with-tooltip';
import { JSONSchema7 } from 'json-schema';
import { Dictionary_unknown_ } from 'src/api/generated/models/Dictionary_unknown_';
import { JSONSchemaForm } from 'src/modules/shared/components/organisms/json-schema-form';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

interface ToolConfigProps {
  selectedTool: TCAction;
  instructions: string;
  setInstructions: (value: string) => void;
  showConfigPanel: boolean;
  configuredInputs: Dictionary_unknown_;
  handleConfigInputsChange: (data: Dictionary_unknown_) => void;
  alias?: string;
  setAlias?: (value: string) => void;
  aliasError?: string;
  showAliasField?: boolean;
  formKey?: string;
}

export const ToolConfig: React.FC<ToolConfigProps> = ({
  selectedTool,
  instructions,
  setInstructions,
  showConfigPanel,
  configuredInputs,
  handleConfigInputsChange,
  alias,
  setAlias,
  aliasError,
  showAliasField,
  formKey,
}) => {
  const { t } = useI18n();

  return (
    <div
      key={formKey}
      className="flex flex-col p-4 max-h-[22rem] overflow-y-auto gap-6"
    >
      <Text size="small" color="neutral">
        {selectedTool.description}
      </Text>

      {setAlias && showAliasField && (
        <WithTooltip
          // TODO: check when implementing i18n
          tooltip="Nombre personalizado para la configuración de esta herramienta. Úsalo si quieres que el agente IA use este nombre para usar esta herramienta."
          align="start"
        >
          <LabelTextInput
            label="Alias de herramienta (Opcional)"
            labelPosition="top"
            inputProps={{
              value: alias ?? '',
              onChange: (e) => setAlias(e.target.value),
              placeholder: t(
                'tools.toolConfig.aliasPlaceholder.listProperties'
              ),
              error: aliasError,
            }}
          />
        </WithTooltip>
      )}

      <WithTooltip
        // TODO: check when implementing i18n
        tooltip="Instrucciones específicas para esta herramienta que guiarán al agente IA en su uso"
        align="start"
      >
        <LabelTextArea
          label="Instrucciones de uso"
          labelPosition="top"
          inputProps={{
            value: instructions,
            onChange: (e) => setInstructions(e.target.value),
            // TODO: check when implementing i18n
            placeholder: 'Instrucciones personalizadas para esta herramienta',
          }}
        />
      </WithTooltip>

      {showConfigPanel && selectedTool.inputSchema && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Text size="paragraph" weight="medium">
              {/* TODO: check when implementing i18n */}
              Parámetros de configuración
            </Text>
            {/* TODO: check when implementing i18n */}
            <WithTooltip tooltip="Configura los valores que serán forzados para esta herramienta. Los campos que dejes sin configurar podrán ser elegidos por el agente IA.">
              <></>
            </WithTooltip>
          </div>
          <div className="p-4 border border-gray-200 rounded-md bg-gray-50">
            <Text size="small" color="neutral" className="mb-4">
              {/* TODO: check when implementing i18n */}
              Los campos que configures aquí serán valores fijos que el agente
              IA usará siempre. Los campos que dejes sin completar permitirán
              que el agente IA elija el valor más adecuado.
            </Text>
            <JSONSchemaForm
              schema={selectedTool.inputSchema as JSONSchema7}
              formData={configuredInputs}
              onChange={handleConfigInputsChange}
              className="mt-2"
              allFieldsOptional={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};
